import React, { useState } from 'react';
import { Tooltip, Modal, Button, Typography } from 'antd';
import { QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text, Paragraph, Title } = Typography;

/**
 * ヘルプアイコンコンポーネント
 * ホバーでツールチップ、クリックでモーダルを表示
 * 
 * @param {string} tooltip - ホバー時に表示する短い説明
 * @param {string} title - モーダルのタイトル
 * @param {React.ReactNode} content - モーダルの詳細説明（文字列またはJSX）
 * @param {string} type - 'info' | 'question' アイコンの種類
 * @param {string} size - 'small' | 'default' アイコンサイズ
 * @param {object} style - カスタムスタイル
 */
const HelpIcon = ({
  tooltip,
  title,
  content,
  type = 'question',
  size = 'small',
  style = {},
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  const IconComponent = type === 'info' ? InfoCircleOutlined : QuestionCircleOutlined;
  
  const iconStyle = {
    color: '#8c8c8c',
    cursor: 'pointer',
    fontSize: size === 'small' ? 14 : 16,
    marginLeft: 4,
    transition: 'color 0.2s',
    ...style,
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (content) {
      setModalVisible(true);
    }
  };

  const icon = (
    <IconComponent
      style={iconStyle}
      onClick={handleClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#1890ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#8c8c8c';
      }}
    />
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} placement="top">
          {icon}
        </Tooltip>
      ) : (
        icon
      )}

      {content && (
        <Modal
          title={
            <span>
              <QuestionCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              {title || 'ヘルプ'}
            </span>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" type="primary" onClick={() => setModalVisible(false)}>
              閉じる
            </Button>,
          ]}
          width={520}
        >
          {typeof content === 'string' ? (
            <Paragraph>{content}</Paragraph>
          ) : (
            content
          )}
        </Modal>
      )}
    </>
  );
};

/**
 * ヘルプテキスト付きのラベルコンポーネント
 * 
 * @param {string} label - ラベルテキスト
 * @param {string} tooltip - ツールチップテキスト
 * @param {string} title - モーダルタイトル
 * @param {React.ReactNode} content - モーダル内容
 * @param {boolean} required - 必須マーク表示
 */
export const LabelWithHelp = ({
  label,
  tooltip,
  title,
  content,
  required = false,
  style = {},
}) => {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', ...style }}>
      {required && <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>}
      <span>{label}</span>
      <HelpIcon tooltip={tooltip} title={title} content={content} />
    </span>
  );
};

/**
 * セクションヘッダー用のヘルプアイコン付きタイトル
 */
export const TitleWithHelp = ({
  children,
  level = 4,
  tooltip,
  title,
  content,
  style = {},
}) => {
  return (
    <Title level={level} style={{ display: 'inline-flex', alignItems: 'center', margin: 0, ...style }}>
      {children}
      <HelpIcon tooltip={tooltip} title={title} content={content} />
    </Title>
  );
};

export default HelpIcon;
